import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDownUp, Filter, X, MapPin, Building2, BedDouble, Euro } from 'lucide-react';
import { usePropertyFilters } from '../../hooks/usePropertyFilters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QuickSearchOptions from './QuickSearchOptions';

interface PropertyFiltersProps {
  onFilterChange: (filters: any) => void;
  activeFilters: any;
  onClearFilters: () => void;
  isHomepage?: boolean;
}

const PropertyFilters: React.FC<PropertyFiltersProps> = ({
  onFilterChange,
  activeFilters,
  onClearFilters,
  isHomepage = false
}) => {
  const { filters, loading } = usePropertyFilters();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tempFilters, setTempFilters] = useState(activeFilters);
  
  // Memoize our handlers to prevent unnecessary re-renders
  const handleFilterChange = useCallback((type: string, value: string) => {
    setTempFilters(prev => ({ ...prev, [type]: value }));
    
    // Immediately apply the filter change for badge removal
    if (!isHomepage) {
      const newFilters = { ...activeFilters, [type]: value };
      onFilterChange(newFilters);
    }
  }, [isHomepage, activeFilters, onFilterChange]);

  const handleSearch = useCallback(() => {
    if (isHomepage) {
      const params = new URLSearchParams();
      Object.entries(tempFilters).forEach(([key, value]) => {
        if (value) params.set(key, value as string);
      });
      navigate(`/immobili?${params.toString()}`);
    } else {
      onFilterChange(tempFilters);
    }
  }, [isHomepage, tempFilters, navigate, onFilterChange]);

  const handleQuickSearch = useCallback((searchFilters: any) => {
    if (isHomepage) {
      const params = new URLSearchParams();
      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value) params.set(key, value as string);
      });
      navigate(`/immobili?${params.toString()}`);
    } else {
      const newFilters = { ...tempFilters, ...searchFilters };
      setTempFilters(newFilters);
      onFilterChange(newFilters);
    }
  }, [isHomepage, tempFilters, navigate, onFilterChange]);

  // Function for removing a single filter
  const handleRemoveFilter = useCallback((key: string) => {
    if (isHomepage) {
      // If on homepage, navigate to the properties page without the filter
      const params = new URLSearchParams();
      // Copy all parameters except the one to remove
      searchParams.forEach((value, paramKey) => {
        if (paramKey !== key) {
          params.set(paramKey, value);
        }
      });
      navigate(`/immobili?${params.toString()}`);
    } else {
      // Create a new filters object without the key to remove
      const newFilters = Object.entries(activeFilters)
        .filter(([k]) => k !== key)
        .reduce((obj, [k, v]) => ({ ...obj, [k]: v }), {});
      
      // If the filter is one of the selectors, reset its value in tempFilters
      if (['zone', 'propertyType', 'priceRange', 'totalRooms'].includes(key)) {
        setTempFilters(prev => ({ ...prev, [key]: '' }));
      }
      
      // Update active filters
      onFilterChange(newFilters);
    }
  }, [isHomepage, activeFilters, navigate, onFilterChange, searchParams]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h2 className="text-lg md:text-xl font-semibold text-primary flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Ricerca Immobili
          </h2>
        </div>
      </div>

      {/* Filters - Always Visible (Mobile & Desktop) */}
      <div className="p-4 md:p-6 space-y-4">
        {/* Filters Grid - Responsive on Mobile */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Zone Select */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-500 mb-1.5 ml-1">
              <MapPin className="inline h-3.5 w-3.5 mr-1" />
              Zona
            </label>
            <select
              className="w-full pl-3 pr-8 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white"
              onChange={(e) => handleFilterChange('zone', e.target.value)}
              value={tempFilters.zone || ''}
              aria-label="Filtra per zona"
            >
              <option value="">Tutte le zone</option>
              {filters.zones.map((zone) => (
                <option key={zone.value} value={zone.value}>
                  {zone.label} ({zone.count})
                </option>
              ))}
            </select>
            <ArrowDownUp className="absolute right-3 top-9 h-5 w-5 text-gray-400 pointer-events-none" />
          </div>

          {/* Property Type Select */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-500 mb-1.5 ml-1">
              <Building2 className="inline h-3.5 w-3.5 mr-1" />
              Tipologia
            </label>
            <select
              className="w-full pl-3 pr-8 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white"
              onChange={(e) => handleFilterChange('propertyType', e.target.value)}
              value={tempFilters.propertyType || ''}
              aria-label="Filtra per tipologia immobile"
            >
              <option value="">Tutte le tipologie</option>
              {filters.propertyTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} ({type.count})
                </option>
              ))}
            </select>
            <ArrowDownUp className="absolute right-3 top-9 h-5 w-5 text-gray-400 pointer-events-none" />
          </div>

          {/* Total Rooms Select */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-500 mb-1.5 ml-1">
              <BedDouble className="inline h-3.5 w-3.5 mr-1" />
              Camere
            </label>
            <select
              className="w-full pl-3 pr-8 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white"
              onChange={(e) => handleFilterChange('totalRooms', e.target.value)}
              value={tempFilters.totalRooms || ''}
              aria-label="Filtra per numero di camere"
            >
              <option value="">Tutte le camere</option>
              {filters.totalRooms.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.count})
                </option>
              ))}
            </select>
            <ArrowDownUp className="absolute right-3 top-9 h-5 w-5 text-gray-400 pointer-events-none" />
          </div>

          {/* Price Range Select */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-500 mb-1.5 ml-1">
              <Euro className="inline h-3.5 w-3.5 mr-1" />
              Prezzo
            </label>
            <select
              className="w-full pl-3 pr-8 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white"
              onChange={(e) => handleFilterChange('priceRange', e.target.value)}
              value={tempFilters.priceRange || ''}
              aria-label="Filtra per fascia di prezzo"
            >
              <option value="">Qualsiasi prezzo</option>
              {filters.priceRanges.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label} ({range.count})
                </option>
              ))}
            </select>
            <ArrowDownUp className="absolute right-3 top-9 h-5 w-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
        
        {/* Quick Search and Buttons */}
        <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center pt-2">
          <div className="hidden md:block">
            <QuickSearchOptions
              onQuickSearch={handleQuickSearch}
              contractType={activeFilters.contractType || "vendita"} // Imposta un valore di default
            />
          </div>
          
          <div className="flex gap-2 w-full md:w-auto">
            {!isHomepage && Object.values(activeFilters).some(v => v) && (
              <button
                onClick={onClearFilters}
                className="flex-1 md:flex-initial py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium text-sm flex items-center justify-center"
              >
                <X className="h-4 w-4 mr-1.5" />
                Cancella
              </button>
            )}
            
            <button
              onClick={handleSearch}
              className="flex-1 md:flex-initial bg-secondary hover:bg-secondary-light text-white font-medium text-sm py-2.5 px-6 rounded-lg transition-colors flex items-center justify-center"
            >
              <Filter className="h-4 w-4 mr-1.5" />
              Cerca
            </button>
          </div>
        </div>
        
        {/* Quick Search Options - Mobile only */}
        <div className="md:hidden mt-2">
          <p className="text-sm font-medium text-gray-700 mb-2">Ricerche rapide:</p>
          <div className="flex flex-wrap gap-2">
            <QuickSearchOptions
              onQuickSearch={handleQuickSearch}
              contractType={activeFilters.contractType || "vendita"} // Imposta un valore di default
              isMobile={true}
            />
          </div>
        </div>
      </div>

      {/* Active Filters badges */}
      <AnimatePresence>
        {!isHomepage && Object.values(activeFilters).some(v => v) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 md:px-6 py-3 border-t border-gray-200 overflow-hidden"
          >
            <div className="flex flex-wrap gap-2">
              {Object.entries(activeFilters).map(([key, value]) => {
                if (!value || key === 'contractType') return null; // Non mostrare il filtro contractType
                
                let filterLabel = '';
                let filterValue = '';
                
                switch (key) {
                  case 'zone':
                    filterLabel = 'Zona';
                    const zoneObj = filters.zones.find(z => z.value === value);
                    filterValue = zoneObj?.label || value as string;
                    break;
                  case 'propertyType':
                    filterLabel = 'Tipologia';
                    const typeObj = filters.propertyTypes.find(t => t.value === value);
                    filterValue = typeObj?.label || value as string;
                    break;
                  case 'priceRange':
                    filterLabel = 'Prezzo';
                    const priceObj = filters.priceRanges.find(p => p.value === value);
                    filterValue = priceObj?.label || value as string;
                    break;
                  case 'totalRooms':
                    filterLabel = 'Camere';
                    const roomsObj = filters.totalRooms.find(r => r.value === value);
                    filterValue = roomsObj?.label || value as string;
                    break;
                }

                if (!filterValue) return null;

                return (
                  <motion.span
                    key={key}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1, transition: { duration: 0.2 } }}
                    exit={{ scale: 0.8, opacity: 0, transition: { duration: 0.2 } }}
                    className="inline-flex items-center bg-gray-100 px-3 py-1.5 rounded-full text-sm hover:bg-gray-200 transition-colors"
                  >
                    <span className="text-gray-500 mr-1">{filterLabel}:</span>
                    <span className="font-medium text-gray-900">{filterValue}</span>
                    <button
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleRemoveFilter(key);
                      }}
                      className="ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-300 p-1 rounded-full transition-colors focus:outline-none flex items-center justify-center w-5 h-5"
                      aria-label={`Rimuovi filtro ${filterLabel}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </motion.span>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PropertyFilters;